import { useCallback } from 'react';

import type {
  GoalSchema,
  CategoryDetails,
  CategoryOption,
} from 'containers/admin/clients/client/tabs-components/goals-tab/types';

export type FieldConfig =
  | CategoryDetails
  | NonNullable<CategoryOption['nested']>;

export type GenerateFieldsFn = (
  config: FieldConfig | null,
  details?: Record<string, unknown>
) => FieldConfig[];

const useGenerateFields = (
  schema: GoalSchema,
  type: string | undefined
): GenerateFieldsFn =>
  useCallback(
    (config, details = {}): FieldConfig[] => {
      if (!config) return [];

      const initialFields: FieldConfig[] = [config];
      let currentConfig: FieldConfig = config;

      const findSelectedOption = (
        options: CategoryOption[],
        associatedPropertyName: string | undefined,
        dataField: Record<string, unknown>
      ): CategoryOption | undefined =>
        options.find((opt) => {
          const fieldValue =
            associatedPropertyName !== undefined
              ? dataField[associatedPropertyName]
              : undefined;

          return Array.isArray(fieldValue)
            ? fieldValue.find((option) => option === opt.value)
            : opt.value === fieldValue;
        });

      Object.keys(details).forEach(() => {
        if (currentConfig?.options) {
          const selectedOption = findSelectedOption(
            currentConfig.options,
            currentConfig.associatedPropertyName,
            details
          );

          if (selectedOption?.nested) {
            initialFields.push(selectedOption.nested);
            currentConfig = selectedOption.nested;
          }
        }
      });

      return initialFields;
    },
    [schema, type]
  );

export default useGenerateFields;
